/**
 * Video renderer.
 *
 * Streams the source in a native `<video controls>` element, centered on a
 * dark stage so letterboxing reads cleanly. Uses a range-capable URL
 * ({@link useFileUrl}) rather than a buffered blob so large videos start
 * playing and seek without downloading the whole file first — same approach
 * as the audio renderer.
 */
import { Loader2 } from "lucide-react";

import { useFileUrl } from "../hooks/use-file-url";
import type { FileRendererProps } from "../types";

export function VideoRenderer({ source }: FileRendererProps) {
  const state = useFileUrl(source);

  if (state.status === "loading" || state.status === "idle") {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p>Unable to play this video file.</p>
        <p className="text-xs opacity-70">{state.error.message}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center bg-slate-900 p-4">
      <video controls src={state.url} className="max-h-full max-w-full rounded shadow-lg">
        Your browser does not support the video element.
      </video>
    </div>
  );
}
